import i18n from '@/common/agent/i18n';
import { format } from 'date-fns';
/**
 * Reference
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
 */

export const toNumberText = (value: number | null, decimalPlace?: number): string => {
  if (value === null) return '';

  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: decimalPlace || 0 }).format(value);
};

export const toCountingPeopleText = (value: number | null): string => {
  if (value === null) return '';
  return i18n.t('common.count.people', { count: toNumberText(value) as any });
};

export const toPercentText = (value: number | null, decimalPlace?: number): string => {
  if (value === null) return '';
  //toNumberText(value, decimalPlace)
  return `${value.toFixed(decimalPlace || 0)}%`;
};

export const toCurrencyText = (value: number | null, decimalPlace?: number): string => {
  if (value === null) return '';
  return i18n.t('common.count.KRW', { count: toNumberText(value, decimalPlace) as any });
};

// Reference: https://date-fns.org/v2.30.0/docs/format
type DateTextPatterns = 'yyyy.MM.dd' | 'yy.MM.dd' | 'yyyy-MM-dd' | 'yyyyMMdd' | 'yyyy-MM-dd HH:mm';
export const toDateText = (value: Date, patterns?: DateTextPatterns): string => {
  return format(value, patterns || 'yyyy.MM.dd');
};

// https://date-fns.org/v2.16.1/docs/format
type TimeTextFormat = 'kk:mm'; // | 'kk:mm:ss';
export const toTimeText = (value: string, format: TimeTextFormat = 'kk:mm') => {
  //TODO: should divide logic if new format id added.
  const [hour, minute] = value.split(':').map((v) => +v);
  if (hour >= 12) {
    const pmText = i18n.t('common.time.pm');
    const hourText = i18n.t('common.time.hour', { count: hour >= 13 ? hour - 12 : hour });
    if (minute > 0) {
      const minuteText = i18n.t('common.time.minute', { count: minute });
      return `${pmText} ${hourText} ${minuteText}`;
    } else {
      return `${pmText} ${hourText}`;
    }
  } else {
    const amText = i18n.t('common.time.am');
    const hourText = i18n.t('common.time.hour', { count: hour });
    if (minute > 0) {
      const minuteText = i18n.t('common.time.minute', { count: minute });
      return `${amText} ${hourText} ${minuteText}`;
    } else {
      return `${amText} ${hourText}`;
    }
  }
};

/**
 * 전화번호에 하이픈을 자동으로 삽입합니다.
 * 식별번호(010, 011, 016, 017, 018, 019, 02, 031, 032 등)에 따라 자릿수가 다르게 처리됩니다.
 * 사용자가 직접 입력한 하이픈도 유지하며, 유효한 패턴(02-, 010-, 031- 등)일 때만 하이픈을 허용합니다.
 *
 * @param value - 전화번호 문자열 (예: "01012345678", "010-", "02-")
 * @returns 하이픈이 삽입된 전화번호 문자열 (예: "010-1234-5678", "02-1234-5678")
 *
 * @example
 * formatPhoneNumber("01012345678") // "010-1234-5678"
 * formatPhoneNumber("010-") // "010-"
 * formatPhoneNumber("02-") // "02-"
 * formatPhoneNumber("031-") // "031-"
 */
export const autoFormatPhoneNumber = (value: string): string => {
  if (!value) return '';

  // 숫자와 하이픈만 허용
  const cleaned = value.replace(/[^\d-]/g, '');

  // 사용자가 직접 입력한 하이픈이 유효한 위치에 있는지 체크
  // 02-, 010-, 011-, 016-, 017-, 018-, 019-, 031-, 032- 등 유효한 패턴
  if (cleaned.endsWith('-')) {
    const beforeHyphen = cleaned.slice(0, -1);
    const numbers = beforeHyphen.replace(/-/g, '');

    // 유효한 패턴: 02-, 010/011/016/017/018/019-, 0XX- (3자리 지역번호)
    if (
      numbers === '02' || // 02-
      /^01[016789]$/.test(numbers) || // 010-, 011-, 016-, 017-, 018-, 019-
      /^0[1-9]{2}$/.test(numbers) // 031-, 032-, 051- 등 3자리 지역번호
    ) {
      return cleaned; // 유효한 하이픈은 그대로 유지
    }
  }

  // 하이픈 제거하여 숫자만 추출
  const numbers = cleaned.replace(/-/g, '');

  if (numbers.length === 0) return '';

  // 010, 011, 016, 017, 018, 019로 시작하는 경우 (11자리)
  if (/^01[016789]/.test(numbers)) {
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }

  // 02로 시작하는 경우 (서울, 9-10자리)
  if (/^02/.test(numbers)) {
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    if (numbers.length <= 9) {
      // 9자리: 02-123-4567
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5, 9)}`;
    }
    // 10자리: 02-1234-5678
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
  }

  // 그 외 지역번호 (031, 032, 033, 041, 042, 043, 051, 052, 053, 054, 055, 061, 062, 063, 064 등, 10-11자리)
  if (/^0/.test(numbers)) {
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    if (numbers.length <= 10) {
      // 10자리: 031-123-4567
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }
    // 11자리: 031-1234-5678
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }

  return numbers;
};

/**
 * 전화번호가 완성된 형태인지 검증합니다.
 * - 휴대폰 (010, 011, 016, 017, 018, 019): 11자리
 * - 서울 (02): 9~10자리
 * - 기타 지역번호 (031, 032 등): 10~11자리
 *
 * @param value - 전화번호 문자열 (예: "010-1234-5678", "02-123-4567")
 * @returns 유효한 전화번호인지 여부
 *
 * @example
 * isValidPhoneNumber("010-1234-5678") // true
 * isValidPhoneNumber("010") // false
 * isValidPhoneNumber("02-123-4567") // true
 * isValidPhoneNumber("031-123-4567") // true
 */
export const isValidPhoneNumber = (value: string): boolean => {
  if (!value) return false;

  const numbers = value.replace(/-/g, '');

  // 휴대폰: 010, 011, 016, 017, 018, 019 - 11자리
  if (/^01[016789]/.test(numbers)) {
    return numbers.length === 11;
  }

  // 서울: 02 - 9~10자리
  if (/^02/.test(numbers)) {
    return numbers.length >= 9 && numbers.length <= 10;
  }

  // 기타 지역번호: 0XX - 10~11자리
  if (/^0[1-9][0-9]/.test(numbers)) {
    return numbers.length >= 10 && numbers.length <= 11;
  }

  return false;
};

export const normalizeLoginId = (value?: string): string => {
  if (!value) return '';
  return value.toLowerCase().trim();
};
